import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { motion } from 'motion/react'
import clsx from 'clsx'
import { ScrollingEllipsisText } from '@/components/ScrollingEllipsisText'
import { ISLAND_MOTION, type IslandMotion } from '@/components/islandPresets'

export interface IslandLabelProps {
  text: string
  /** Driven by the owning card's hover/focus. Omit to expand on the label's own hover. */
  expanded?: boolean
  /** Own-hover mode: invisible margin (px) around the small pill that also triggers it */
  hitSlop?: number
  /** Corner the pill is anchored to — it grows away from this side */
  align: 'left' | 'right'
  /** Typography of the small always-on label */
  restClassName: string
  restHeight: number
  restPadX?: number
  restMaxWidth?: number
  restRadius?: number
  /** Typography of the enlarged label (size comes from expandedFontPx) */
  expandedClassName?: string
  expandedFontPx: number
  expandedPadX?: number
  /** Defaults to 2× the font size */
  expandedHeight?: number
  /** Floor for the enlarged pill so 1–2 character labels stay landscape instead of
   *  growing taller than wide (which read as the pill jutting upward) */
  expandedMinWidth?: number
  /** Moderately rounded by default — a full pill read as too bubbly next to the 4px chips */
  expandedRadius?: number
  /** Cap for the enlarged pill; text that still doesn't fit scrolls across.
   *  Omit for short labels (ranks, scores) that always fit. */
  expandedMaxWidth?: number
  motionTuning?: IslandMotion
  /** Position within the card, e.g. "bottom-2 right-2" */
  className?: string
  /** Pill colors, e.g. "bg-black/60 text-white" */
  pillClassName?: string
}

/** A small corner label that grows Dynamic-Island style (minus the full pill shape) while its card is hovered:
 *  the pill springs wider/taller from its own corner, the tiny label cross-fades
 *  into a readable one, and (with `expandedMaxWidth`) a string that still doesn't
 *  fit scrolls across once the pill has settled.
 *
 *  Sizes are measured and animated explicitly (no Motion `layout`) so this stays
 *  safe inside the virtualized rails. */
export function IslandLabel({
  text,
  expanded: expandedProp,
  hitSlop = 6,
  align,
  restClassName,
  restHeight,
  restPadX = 4,
  restMaxWidth,
  restRadius = 4,
  expandedClassName,
  expandedFontPx,
  expandedPadX = 10,
  expandedHeight,
  expandedMinWidth = 0,
  expandedRadius = 6,
  expandedMaxWidth,
  motionTuning = ISLAND_MOTION,
  className,
  pillClassName,
}: IslandLabelProps) {
  const [selfHovered, setSelfHovered] = useState(false)
  const selfDriven = expandedProp === undefined
  const expanded = expandedProp ?? selfHovered
  // Leaving collapses after a short grace period, and re-entering cancels it:
  // while the pill re-lays out (and where the rail clips it) the pointer can
  // briefly fall outside the hit box, which otherwise flickers open/closed.
  const leaveTimer = useRef(0)
  const onEnter = () => {
    window.clearTimeout(leaveTimer.current)
    setSelfHovered(true)
  }
  const onLeave = () => {
    window.clearTimeout(leaveTimer.current)
    leaveTimer.current = window.setTimeout(() => setSelfHovered(false), 120)
  }
  useEffect(() => () => window.clearTimeout(leaveTimer.current), [])
  const restRef = useRef<HTMLSpanElement>(null)
  const largeRef = useRef<HTMLSpanElement>(null)
  const [natural, setNatural] = useState({ rest: 0, large: 0 })

  useLayoutEffect(() => {
    const rest = restRef.current
    const large = largeRef.current
    if (!rest || !large) return
    // Fractional widths rounded UP — offsetWidth rounds to nearest, which can leave the
    // pill a sub-pixel too narrow and ellipsize a label that was meant to fit exactly
    const width = (el: HTMLElement) => Math.ceil(el.getBoundingClientRect().width)
    const measure = () => setNatural({ rest: width(rest), large: width(large) })
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(rest)
    observer.observe(large)
    return () => observer.disconnect()
  }, [text, expandedFontPx])

  const restW = Math.min(natural.rest + restPadX * 2, restMaxWidth ?? Infinity)
  const expandedW = Math.max(
    expandedMinWidth,
    Math.min(natural.large + expandedPadX * 2, expandedMaxWidth ?? Infinity)
  )
  const expandedH = expandedHeight ?? Math.round(expandedFontPx * 2)
  // Centers a short label inside a min-width pill; equals expandedPadX otherwise
  const expandedInset = Math.max(expandedPadX, (expandedW - natural.large) / 2)

  const { visualDuration, bounce, slow } = motionTuning
  const spring = { type: 'spring' as const, visualDuration: visualDuration * slow, bounce }
  const crossfade = { duration: 0.18 * slow, ease: 'easeOut' as const }
  // Both labels are pinned to the pill's anchored edge so neither slides while it grows
  const pin = (pad: number) => (align === 'right' ? { right: pad } : { left: pad })

  return (
    <div
      className={clsx('absolute', className)}
      // Negative margin + equal padding: a bigger hover target, same pill position
      style={selfDriven ? { padding: hitSlop, margin: -hitSlop } : undefined}
      onPointerEnter={selfDriven ? onEnter : undefined}
      onPointerLeave={selfDriven ? onLeave : undefined}
    >
      <motion.div
        initial={false}
        animate={{
          width: expanded ? expandedW : restW,
          height: expanded ? expandedH : restHeight,
          borderRadius: expanded ? expandedRadius : restRadius,
        }}
        transition={spring}
        className={clsx('relative overflow-hidden', pillClassName)}
      >
        {/* Invisible measurers for each label's natural width */}
        <span
          ref={restRef}
          aria-hidden="true"
          className={clsx('invisible absolute whitespace-nowrap', restClassName)}
        >
          {text}
        </span>
        <span
          ref={largeRef}
          aria-hidden="true"
          className={clsx('invisible absolute whitespace-nowrap', expandedClassName)}
          style={{ fontSize: expandedFontPx }}
        >
          {text}
        </span>

        <motion.span
          aria-hidden={expanded}
          initial={false}
          animate={{ opacity: expanded ? 0 : 1, filter: expanded ? 'blur(3px)' : 'blur(0px)' }}
          transition={crossfade}
          className={clsx(
            'absolute inset-y-0 flex items-center overflow-hidden whitespace-nowrap',
            restClassName
          )}
          style={{ ...pin(restPadX), width: restW - restPadX * 2 }}
        >
          {text}
        </motion.span>
        <motion.span
          aria-hidden={!expanded}
          initial={false}
          animate={{ opacity: expanded ? 1 : 0, filter: expanded ? 'blur(0px)' : 'blur(3px)' }}
          transition={{ ...crossfade, delay: expanded ? 0.06 * slow : 0 }}
          className={clsx('absolute inset-y-0 flex items-center whitespace-nowrap', expandedClassName)}
          style={{
            ...pin(expandedInset),
            width: expandedW - expandedInset * 2,
            fontSize: expandedFontPx,
          }}
        >
          {expandedMaxWidth === undefined ? (
            text
          ) : (
            <ScrollingEllipsisText
              text={text}
              active={expanded}
              // Let the pill finish growing before the text starts to travel
              delayMs={visualDuration * slow * 1600}
              msPerPx={25 * slow}
              returnMs={1000 * slow}
              className="w-full"
            />
          )}
        </motion.span>
      </motion.div>
    </div>
  )
}
